/**
 * GanttProfile — task-bar scheduling with milestones, dependencies,
 * and section swimlanes.
 *
 * Scaffold profile for the Gantt domain. Epic SP-A (#1386) fills in
 * the 5 rendering modes (gantt, timeline, roadmap, sprint, milestone),
 * critical-path computation, and resource swimlane views.
 *
 * See plans/strategy-planning/domain.md § 1 for the canonical Gantt
 * grammar and view profiles.
 */

import type { DomainDataProfile } from '../../../core/data-profiles';
import type { DiagramType } from '../../../lib/diagram/registries';

export type GanttNodeType = 'gantt_task' | 'gantt_milestone' | 'gantt_section';
export type GanttEdgeType =
  | 'finish_to_start'
  | 'start_to_start'
  | 'finish_to_finish'
  | 'start_to_finish'
  | 'milestone_gate'
  | 'section_contains';

export const GanttProfile: DomainDataProfile<GanttNodeType, GanttEdgeType> = {
  domainId: 'gantt',
  profileVersion: '1.0',
  // Concrete RDM @type is 'gantt', but the canvas/picker slot is the
  // canonical strategy-planning diagram type.
  diagramTypes: ['planning' as DiagramType],

  nodeTypes: ['gantt_task', 'gantt_milestone', 'gantt_section'],
  edgeTypes: [
    'finish_to_start',
    'start_to_start',
    'finish_to_finish',
    'start_to_finish',
    'milestone_gate',
    'section_contains',
  ],

  vocabulary: {
    recordSingular: 'Task',
    recordPlural: 'Tasks',
    repositoryLabel: 'Project',
    hierarchyLabel: 'Dependencies',
    importAction: 'Import Plan',
    keyColumnHint: 'Task Name',
    commitLabel: 'Update Plan',
    pullLabel: 'Refresh Plan',
    diagramLabel: 'Gantt chart',
  },

  fieldAliases: {
    name: ['task', 'title'],
    duration: ['length', 'estimate', 'est'],
    start: ['startDate', 'begin'],
    end: ['endDate', 'finish'],
    after: ['depends_on', 'predecessor'],
    progress: ['percent', 'pct', 'completion'],
    assignee: ['owner', 'responsible'],
  },

  requiredProps: ['name'],
  recommendedProps: ['duration', 'progress'],

  primaryShapeType: 'gantt_task',

  propsToAttrsMapping: {
    name: 'name',
    duration: 'duration',
    start: 'start',
    end: 'end',
    after: 'after',
    progress: 'progress',
    status: 'status',
    assignee: 'assignee',
    section: 'section',
  },

  relationshipPackId: 'planning-relationships',
};
